import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60';

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-10 px-5 text-sm',
};

// Gradient Stripe 2024 : décalage subtil de luminosité de haut en bas (6 %),
// ombre `sm` pour la profondeur. Au hover, le gradient devient plus dense
// (accent → accent-hover) et l'ombre plus marquée.
const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-b from-[#5476E8] to-accent text-white shadow-sm hover:from-accent hover:to-accent-hover hover:shadow',
  secondary: 'bg-white border border-border-subtle text-brand shadow-sm hover:bg-gray-50',
  ghost: 'text-brand hover:bg-gray-100',
  danger: 'bg-white border border-danger-100 text-danger hover:bg-danger-50',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading,
    children,
    className,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={cn(base, sizes[size], variants[variant], className)}
      {...rest}
    >
      {leadingIcon && <span className="-ml-0.5 flex items-center">{leadingIcon}</span>}
      {children}
      {trailingIcon && <span className="-mr-0.5 flex items-center">{trailingIcon}</span>}
    </button>
  );
});
